import {
  Create,
  CreateActions,
  FileInput,
  required,
  SimpleForm,
  TextInput,
} from "react-admin";
import { DatePicker } from "@material-ui/pickers";
import { useNotification } from "../../../../common/hooks/useNotification";
import { useForm } from "react-final-form";
import { useAxiosInstance } from "../../../../common/hooks/useAxiosInstance";
import { useState } from "react";
import { DropEvent } from "react-dropzone";
import { UploadedFileField } from "../../../common/form/UploadedFileField";
import { GenericDateOrTimePicker } from "../../../common/form/GenericDateOrTimePicker";
import {
  Configuration,
  EldFileImportApiFactory,
} from "../../../../common/data/service-proxies";
import { FormApi } from "final-form";
import { AxiosInstance } from "axios";

type UploadStatus = "none" | "uploading" | "uploaded" | "failed";
interface IHandleFileDropProps {
  form: FormApi<Record<string, any>, Partial<Record<string, any>>>;
  files: File[];
  event: DropEvent;
  axiosInstance: AxiosInstance | null;
  onUploadFailed?: (error: any) => void;
  onUploadSucceeded?: () => void;
  onUploadProgress?: (event: ProgressEvent) => void;
}

const parseSelectedDate = (value: unknown) => {
  if (value instanceof Date) {
    return Number.isNaN(value.getTime()) ? null : value;
  }

  if (!value) {
    return null;
  }

  const parsed = new Date(`${value}`);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
};

const toLocalMidnightIsoString = (value: unknown) => {
  const parsedDate = parseSelectedDate(value);

  if (!parsedDate) {
    return null;
  }

  return new Date(
    parsedDate.getFullYear(),
    parsedDate.getMonth(),
    parsedDate.getDate(),
    0,
    0,
    0,
    0
  ).toISOString();
};

const validateImportDateRange = (values: Record<string, any>) => {
  const errors: Record<string, string> = {};
  const importDateFrom = parseSelectedDate(values.importDateFrom);
  const importDateTo = parseSelectedDate(values.importDateTo);

  if (
    importDateFrom &&
    importDateTo &&
    importDateFrom.getTime() > importDateTo.getTime()
  ) {
    const errorMessage = "Import date from cannot be later than import date to.";
    errors.importDateFrom = errorMessage;
    errors.importDateTo = errorMessage;
  }

  return errors;
};

export const EldImportCreate = (props: any) => {
  const notify = useNotification(props, "createSuccess");

  return (
    <Create
      {...props}
      actions={<CreateActions />}
      onSuccess={notify}
      transform={(data) => ({
        ...data,
        importDateFrom: toLocalMidnightIsoString(data.importDateFrom),
        importDateTo: toLocalMidnightIsoString(data.importDateTo),
      })}
      title="resources.admin-eld-import.title.create"
    >
      <EldImportCreateForm />
    </Create>
  );
};

const handleFileDrop = async ({
  form,
  files,
  event,
  axiosInstance,
  onUploadFailed,
  onUploadSucceeded,
  onUploadProgress,
}: IHandleFileDropProps) => {
  if (!axiosInstance) {
    console.error("Dropped a file but the Axios instance was undefined: ", {
      files,
      event,
    });

    if (onUploadFailed) {
      onUploadFailed("Dropped a file but the Axios instance was undefined");
    }

    return;
  }

  const cfg: Configuration = new Configuration();
  cfg.baseOptions = {
    headers: {
      "Content-Type": "multipart/form-data",
    },
    onUploadProgress: onUploadProgress,
  };
  try {
    const response = await EldFileImportApiFactory(
      cfg,
      axiosInstance.defaults.baseURL,
      axiosInstance
    ).apiAppEldfileimportUploadPost(files[0]);

    form.change("eldFileUploadId", response.data);
    form.change("fileName", files[0].name);
    if (onUploadSucceeded) {
      onUploadSucceeded();
    }
  } catch (e) {
    console.error("Upload failed: ", e);
    if (onUploadFailed) {
      onUploadFailed(e);
    }
  }
};

const EldImportCreateForm = (props: any) => {
  return (
    <SimpleForm
      className="form-create-edit"
      variant="outlined"
      validate={validateImportDateRange}
      {...props}
    >
      <EldFileInput />
      <GenericDateOrTimePicker
        PickerComponent={DatePicker}
        source="importDateFrom"
        label="resources.admin-eld-import.labels.dateFrom"
        options={{
          autoOk: true,
          format: "MMMM do yyyy",
        }}
        validate={required()}
      />
      <GenericDateOrTimePicker
        PickerComponent={DatePicker}
        source="importDateTo"
        label="resources.admin-eld-import.labels.dateTo"
        options={{
          autoOk: true,
          format: "MMMM do yyyy",
        }}
        validate={required()}
      />
      <TextInput source="description" validate={required()} />
    </SimpleForm>
  );
};

const EldFileInput = (props: any) => {
  const axiosInstance = useAxiosInstance();
  const [uploadStatus, setUploadStatus] = useState<UploadStatus>("none");
  const form = useForm();

  return (
    <FileInput
      {...props}
      isRequired
      validate={required()}
      source="file"
      options={{
        disabled: uploadStatus === "uploading",
        onDropAccepted: (files: File[], event: DropEvent) => {
          setUploadStatus("uploading");
          handleFileDrop({
            form,
            files,
            event,
            axiosInstance,
            onUploadFailed: () => {
              setUploadStatus("failed");
            },
            onUploadSucceeded: () => {
              setUploadStatus("uploaded");
            },
          });
        },
      }}
    >
      <UploadedFileField uploadStatus={uploadStatus} source="src" />
    </FileInput>
  );
};
